import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { ConvertedFile } from '../../models/converted-file';
import {
  concatMap,
  delay,
  EMPTY,
  firstValueFrom,
  interval,
  lastValueFrom,
  map,
  mergeMap,
  Observable,
  of,
  skipWhile,
  Subject,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { IndexedDbService } from './indexed-db.service';
import {
  addConvertedFile,
  deleteConvertedFile,
  gerberConversionComplete,
  lectraConversionComplete,
  lockGerber,
  lockLectra,
  moveFileToEndOfGerberQueue,
  moveFileToEndOfLectraQueue,
  resetAllGerberFiles,
  resetAllLectraFiles,
  unlockGerber,
  unlockLectra,
  updateGerberFileStatus,
  updateLectraFileStatus,
} from '../../state/home.actions';
import { selectConvertedFiles } from '../../state/home.selectors';
import { TranslateService } from '@ngx-translate/core';
import { UploadedFile } from '../../models/uploaded-file';
import { JobStatus } from '../../models/job-status';
import {
  ConversionResponse,
  ConversionStatusResponse,
} from '../../models/conversion-job';
import { mapStatus } from '../../../../common/helpers/conversion-utils';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private baseUrl = environment.apiUrl;
  private http = inject(HttpClient);
  private toastr = inject(ToastrService);
  private store = inject(Store);
  private indexedDbService = inject(IndexedDbService);
  private translate = inject(TranslateService);
  private cancelProcessing$ = new Subject<void>();
  private cancelGerberProcessing$ = new Subject<void>();
  private isGerberProcessingCancelled = false;
  private cancelLectraProcessing$ = new Subject<void>();
  private isLectraProcessingCancelled = false;

  getMonthlyConvertedFiles(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      this.baseUrl + 'api/files/user/converted-monthly-by-company',
    );
  }

  getAvailableConvertedFiles(): Observable<ConvertedFile[]> {
    return this.http
      .get<{
        files: ConvertedFile[];
      }>(`${this.baseUrl}api/files/user/available`)
      .pipe(
        map((response) =>
          response.files.map(
            (file): ConvertedFile => ({
              fileId: file.fileId,
              jobId: file.jobId,
              fileName: file.fileName,
              convertedFileName: file.convertedFileName,
              fileUrl: file.fileUrl,
              fileSize: file.fileSize,
              status: JobStatus.Complete,
            }),
          ),
        ),
      );
  }

  deleteConvertedFile(fileId: string) {
    const params = new HttpParams().set('fileId', fileId);

    return this.http.delete(`${this.baseUrl}api/files/user/delete`, { params });
  }

  completeGerberFileProcessing() {
    if (!this.isGerberProcessingCancelled) {
      this.toastr.info(this.translate.instant('toastr.completed'));
    }

    this.isGerberProcessingCancelled = true;
    this.cancelGerberProcessing$.next();
    this.store.dispatch(unlockGerber());
    this.store.dispatch(resetAllGerberFiles());
  }

  completeLectraFileProcessing() {
    if (!this.isLectraProcessingCancelled) {
      this.toastr.info(this.translate.instant('toastr.completed'));
    }

    this.isLectraProcessingCancelled = true;
    this.cancelLectraProcessing$.next();
    this.store.dispatch(unlockLectra());
    this.store.dispatch(resetAllLectraFiles());
  }

  cancelGerberFileProcessing() {
    if (this.isGerberProcessingCancelled) {
      return;
    }
    this.isGerberProcessingCancelled = true;
    this.cancelGerberProcessing$.next();
    this.store.dispatch(unlockGerber());
    this.store.dispatch(resetAllGerberFiles());

    this.toastr.info(this.translate.instant('toastr.cancelled'));
  }

  cancelLectraFileProcessing() {
    if (this.isLectraProcessingCancelled) {
      return;
    }
    this.isLectraProcessingCancelled = true;
    this.cancelLectraProcessing$.next();
    this.store.dispatch(unlockLectra());
    this.store.dispatch(resetAllLectraFiles());

    this.toastr.info(this.translate.instant('toastr.cancelled'));
  }

  cancelAllProcessing() {
    const wasAnyProcessing =
      !this.isGerberProcessingCancelled || !this.isLectraProcessingCancelled;

    if (!this.isGerberProcessingCancelled) {
      this.isGerberProcessingCancelled = true;
      this.cancelGerberProcessing$.next();
      this.store.dispatch(unlockGerber());
      this.store.dispatch(resetAllGerberFiles());
    }

    if (!this.isLectraProcessingCancelled) {
      this.isLectraProcessingCancelled = true;
      this.cancelLectraProcessing$.next();
      this.store.dispatch(unlockLectra());
      this.store.dispatch(resetAllLectraFiles());
    }

    if (wasAnyProcessing) {
      this.toastr.info(this.translate.instant('toastr.cancelled'));
    }
  }

  processGerberFileQueue(uploadedFiles$: Observable<UploadedFile[]>) {
    uploadedFiles$.pipe(take(1)).subscribe((files) => {
      if (files.length === 0) {
        return;
      }

      this.isGerberProcessingCancelled = false;
      this.cancelGerberProcessing$ = new Subject<void>();
      this.store.dispatch(lockGerber());

      const reordered = [
        ...files.filter((f) => f.status !== JobStatus.Error),
        ...files.filter((f) => f.status === JobStatus.Error),
      ];

      of(...reordered)
        .pipe(
          concatMap((file) => {
            if (
              this.isGerberProcessingCancelled ||
              file.status === JobStatus.Error
            ) {
              return EMPTY;
            }

            return this.processGerberFile(file).pipe(
              takeUntil(this.cancelGerberProcessing$),
              mergeMap((success) => {
                if (!success && !this.isGerberProcessingCancelled) {
                  this.store.dispatch(
                    moveFileToEndOfGerberQueue({ fileId: file.fileId }),
                  );
                  return of(file).pipe(delay(5000));
                }
                return EMPTY;
              }),
            );
          }),
          takeUntil(this.cancelGerberProcessing$),
        )
        .subscribe({
          complete: () => {
            if (!this.isGerberProcessingCancelled) {
              this.completeGerberFileProcessing();
            }
          },
          error: () => {
            this.store.dispatch(unlockGerber());
          },
        });
    });
  }

  processLectraFileQueue(uploadedFiles$: Observable<UploadedFile[]>) {
    uploadedFiles$.pipe(take(1)).subscribe((files) => {
      if (files.length === 0) {
        return;
      }

      this.isLectraProcessingCancelled = false;
      this.cancelLectraProcessing$ = new Subject<void>();
      this.store.dispatch(lockLectra());

      const reordered = [
        ...files.filter((f) => f.status !== JobStatus.Error),
        ...files.filter((f) => f.status === JobStatus.Error),
      ];

      of(...reordered)
        .pipe(
          concatMap((file) => {
            if (
              this.isLectraProcessingCancelled ||
              file.status === JobStatus.Error
            ) {
              return EMPTY;
            }

            return this.processLectraFile(file).pipe(
              takeUntil(this.cancelLectraProcessing$),
              mergeMap((success) => {
                if (!success && !this.isLectraProcessingCancelled) {
                  this.store.dispatch(
                    moveFileToEndOfLectraQueue({ fileId: file.fileId }),
                  );
                  return of(file).pipe(delay(5000));
                }
                return EMPTY;
              }),
            );
          }),
          takeUntil(this.cancelLectraProcessing$),
        )
        .subscribe({
          complete: () => {
            if (!this.isLectraProcessingCancelled) {
              this.completeLectraFileProcessing();
            }
          },
          error: () => {
            this.store.dispatch(unlockLectra());
          },
        });
    });
  }

  startMonitoring(interval: number = 60000) {
    this.checkFilesExistence();

    setInterval(() => {
      this.checkFilesExistence();
    }, interval);
  }

  async downloadConvertedFile(fileId: string) {
    const url = `${this.baseUrl}api/Blob/download?fileId=${fileId}`;

    const response = await lastValueFrom(
      this.http.get(url, {
        observe: 'response',
        responseType: 'blob',
      }),
    );
    const blobBody = response.body;
    if (!blobBody) {
      throw new Error('Empty response body');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = /filename="?([^"]+)"?/.exec(contentDisposition || '');
    const filename = filenameMatch?.[1] || 'downloaded-file';

    const downloadUrl = window.URL.createObjectURL(blobBody);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  }

  private checkFilesExistence() {
    this.store
      .select(selectConvertedFiles)
      .pipe(take(1))
      .subscribe((files) => {
        files.forEach((file) => {
          this.http
            .head(`${this.baseUrl}api/Blob/exists`, {
              params: { fileId: file.fileId },
              observe: 'response',
            })
            .subscribe({
              error: (err) => {
                if (err.status === 404) {
                  console.warn(
                    `File ${file.fileName} not found on Azure (404). Removing...`,
                  );
                  this.store.dispatch(
                    deleteConvertedFile({ fileId: file.fileId }),
                  );
                } else {
                  console.warn(
                    `HEAD failed for ${file.fileName} with status ${err.status}, not removing.`,
                  );
                }
              },
            });
        });
      });
  }

  private createGerberConversion(
    formData: FormData,
  ): Observable<ConversionResponse> {
    return this.http.post<ConversionResponse>(
      `${this.baseUrl}api/ConversionJob/create-gerber-conversion`,
      formData,
    );
  }

  private createLectraConversion(
    formData: FormData,
  ): Observable<ConversionResponse> {
    return this.http.post<ConversionResponse>(
      `${this.baseUrl}api/ConversionJob/create-lectra-conversion`,
      formData,
    );
  }

  private async addConvertedFileWithLimit(file: ConvertedFile) {
    const convertedFiles = await firstValueFrom(
      this.store.select(selectConvertedFiles),
    );

    const MAX = 50;
    if (convertedFiles.length >= MAX) {
      const oldestFile = convertedFiles[0];
      this.store.dispatch(deleteConvertedFile({ fileId: oldestFile.fileId }));
      await firstValueFrom(
        this.store.select(selectConvertedFiles).pipe(
          skipWhile((files) =>
            files.some((f) => f.fileId === oldestFile.fileId),
          ),
          take(1),
        ),
      );
    }

    this.store.dispatch(addConvertedFile({ file }));
  }

  private processGerberFile(file: UploadedFile): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      if (this.isGerberProcessingCancelled) {
        subscriber.next(false);
        subscriber.complete();
        return;
      }

      this.indexedDbService
        .getFileData(file.fileId)
        .then((fileData) => {
          if (!fileData?.data) throw new Error('Missing file data');

          const formData = new FormData();
          formData.append('fileData', fileData.data, file.fileName);

          this.createGerberConversion(formData)
            .pipe(takeUntil(this.cancelGerberProcessing$))
            .subscribe({
              next: (conversion) => {
                this.pollConversionStatus(conversion.id)
                  .pipe(takeUntil(this.cancelProcessing$))
                  .subscribe({
                    next: async (res) => {
                      const status = mapStatus(res.conversionStatus.status);

                      this.store.dispatch(
                        updateGerberFileStatus({
                          fileId: file.fileId,
                          status: status,
                        }),
                      );

                      if (status == JobStatus.Complete) {
                        this.http
                          .get<ConvertedFile>(
                            `${this.baseUrl}api/ConversionJob/converted-file?conversionId=${conversion.id}`,
                          )
                          .pipe(takeUntil(this.cancelGerberProcessing$))
                          .subscribe(async (converted) => {
                            await this.addConvertedFileWithLimit(converted);
                            this.store.dispatch(
                              gerberConversionComplete({
                                fileId: file.fileId,
                              }),
                            );
                            subscriber.next(true);
                            subscriber.complete();
                          });
                      }

                      if (status == JobStatus.Error) {
                        this.toastr.error(
                          this.translate.instant(
                            'toastr.errorWhileProcessing',
                            {
                              fileName: file.fileName,
                            },
                          ),
                        );
                        subscriber.next(false);
                        subscriber.complete();
                      }
                    },
                    error: (err) => {
                      console.error('Polling error:', err);
                      subscriber.next(false);
                      subscriber.complete();
                    },
                    complete: () => {
                      if (this.isGerberProcessingCancelled) {
                        subscriber.next(false);
                        subscriber.complete();
                      }
                    },
                  });
              },
              error: (err) => {
                console.error('Error starting .zip conversion:', err);
                subscriber.next(false);
                subscriber.complete();
              },
            });
        })
        .catch((error) => {
          console.error('Error with indexed db data:', error);
          subscriber.next(false);
          subscriber.complete();
        });
    });
  }

  private processLectraFile(file: UploadedFile): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      if (this.isLectraProcessingCancelled) {
        subscriber.next(false);
        subscriber.complete();
        return;
      }

      this.indexedDbService
        .getFileData(file.fileId)
        .then((fileData) => {
          if (!fileData?.data) throw new Error('Missing file data');

          const formData = new FormData();
          formData.append('fileData', fileData.data, file.fileName);

          this.createLectraConversion(formData)
            .pipe(takeUntil(this.cancelLectraProcessing$))
            .subscribe({
              next: (conversion) => {
                this.pollConversionStatus(conversion.id)
                  .pipe(takeUntil(this.cancelLectraProcessing$))
                  .subscribe({
                    next: async (res) => {
                      const status = mapStatus(res.conversionStatus.status);

                      this.store.dispatch(
                        updateLectraFileStatus({
                          fileId: file.fileId,
                          status: status,
                        }),
                      );

                      if (status == JobStatus.Complete) {
                        this.http
                          .get<ConvertedFile>(
                            `${this.baseUrl}api/ConversionJob/converted-file?conversionId=${conversion.id}`,
                          )
                          .pipe(takeUntil(this.cancelLectraProcessing$))
                          .subscribe(async (converted) => {
                            await this.addConvertedFileWithLimit(converted);
                            this.store.dispatch(
                              lectraConversionComplete({
                                fileId: file.fileId,
                              }),
                            );
                            subscriber.next(true);
                            subscriber.complete();
                          });
                      }

                      if (status == JobStatus.Error) {
                        this.toastr.error(
                          this.translate.instant(
                            'toastr.errorWhileProcessing',
                            {
                              fileName: file.fileName,
                            },
                          ),
                        );
                        subscriber.next(false);
                        subscriber.complete();
                      }
                    },
                    error: (err) => {
                      console.error('Polling error:', err);
                      subscriber.next(false);
                      subscriber.complete();
                    },
                    complete: () => {
                      if (this.isLectraProcessingCancelled) {
                        subscriber.next(false);
                        subscriber.complete();
                      }
                    },
                  });
              },
              error: (err) => {
                console.error('Error starting .mdl conversion:', err);
                subscriber.next(false);
                subscriber.complete();
              },
            });
        })
        .catch((error) => {
          console.error('Error with indexed db data:', error);
          subscriber.next(false);
          subscriber.complete();
        });
    });
  }

  private pollConversionStatus(
    conversionId: string,
    intervalMs = 5000,
  ): Observable<ConversionStatusResponse> {
    return interval(intervalMs).pipe(
      takeUntil(this.cancelProcessing$),
      mergeMap(() =>
        this.http.get<ConversionStatusResponse>(
          `${this.baseUrl}api/ConversionJob/status?conversionId=${conversionId}`,
        ),
      ),
      takeWhile((res) => {
        const status = mapStatus(res.conversionStatus.status);
        return status === JobStatus.Processing || status === JobStatus.Wait;
      }, true),
    );
  }
}
